import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SunLoader } from '../components/SunLoader.js';
import { BackIcon } from '../player/PlayerIcons.js';
import {
  LAUNCH_CLOSE_MS,
  LAUNCH_REVEAL_MS,
  cancelLaunch,
  finishLaunch,
  useLaunchView,
} from './launch-store.js';
import {
  PROBE_DELAY_MS,
  PROBE_FRAMES,
  rememberPlain,
  rememberedScrim,
  slowFrames,
  weakDevice,
} from './scrim-mode.js';

/** Клавиши, которыми страница прокручивается или нажимается под слоем. */
const PAGE_KEYS = new Set([
  ' ', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End',
]);
/** «Назад» пульта и клавиатуры. */
const BACK_KEYS = new Set(['Escape', 'BrowserBack', 'GoBack']);

/**
 * Слой запуска: экран загрузки плеера, который приходит раньше плеера.
 *
 * Нажали серию, озвучку или раздачу — слой уже на экране: страница под ним
 * притемнена и размыта, по центру ЭКРАНА (а не содержимого — боковое меню
 * тоже под слоем) стандартный знак ожидания, под ним — что запускается и на
 * каком этапе. Дальше под слоем меняется экран: страница растворяется в
 * чёрный экран плеера, а знак всё это время стоит на месте. Первый кадр —
 * слой растворяется над видео.
 *
 * Знак — один и тот же элемент от нажатия до первого кадра: поэтому он не
 * прыгает и не мигает между маршрутами. Свой экран загрузки плеера
 * (буферизация позже, в ходе просмотра) стоит на том же месте и того же
 * размера (`.screen-wait`, styles.css).
 *
 * Кнопка «назад» — та же и там же, что в плеере: во время подготовки она
 * отменяет запуск, над плеером — уводит с него, как его собственная.
 *
 * Запуск не удался — слой не исчезает молча: знак гаснет, на месте этапа
 * встаёт причина, и через несколько секунд (или по любому нажатию) слой
 * уходит, вернув страницу и фокус на нажатый элемент.
 *
 * Живёт в оболочке, над маршрутами (`RootApp`): страница, начавшая запуск,
 * размонтируется при переходе на плеер, слой — нет.
 */
export function LaunchLayer(): React.JSX.Element | null {
  const view = useLaunchView();
  const host = useRef<HTMLDivElement>(null);
  const back = useRef<HTMLButtonElement>(null);
  // Размытие под слоем или плотное притемнение (`scrim-mode.ts`).
  const [plain, setPlain] = useState(() => rememberedScrim() === 'plain' || weakDevice(window.navigator));

  const id = view?.id ?? null;
  const phase = view?.phase ?? null;
  const blocking = phase === 'preparing' || phase === 'handoff' || phase === 'failed';

  // Фокус уходит в слой в том же кадре, что и нажатие: страница под ним больше
  // не принимает ввод. На сам слой, а не на кнопку «назад» — иначе повторное
  // «ОК» пульта отменяло бы запуск, который только что начали.
  useLayoutEffect(() => {
    if (id === null) return;
    host.current?.focus({ preventScroll: true });
  }, [id]);

  // Ввод, пока слой закрывает экран: «назад» отменяет, Tab не выходит за слой,
  // страница под слоем не прокручивается и не нажимается.
  useEffect(() => {
    if (!blocking) return undefined;
    const layer = host.current;
    const onKey = (event: KeyboardEvent): void => {
      if (BACK_KEYS.has(event.key)) {
        // Плеер под слоем слушает Esc сам — второй раз уходить незачем.
        event.preventDefault();
        event.stopPropagation();
        cancelLaunch();
        return;
      }
      if (event.key === 'Tab') {
        // В слое одна кнопка: Tab ведёт на неё и дальше не идёт.
        event.preventDefault();
        event.stopPropagation();
        back.current?.focus({ preventScroll: true });
        return;
      }
      // Над плеером остальные клавиши — его: он слушает их и на своём экране загрузки.
      if (phase === 'handoff' || event.ctrlKey || event.metaKey || event.altKey) return;
      if (!PAGE_KEYS.has(event.key)) return;
      // Кнопка слоя нажимается как обычно; всё прочее до страницы не доходит.
      if (event.target === back.current && (event.key === 'Enter' || event.key === ' ')) return;
      event.preventDefault();
      event.stopPropagation();
      // Причину отказа прочли — «ОК» и пробел убирают слой, как и «назад».
      if (phase === 'failed' && (event.key === 'Enter' || event.key === ' ')) cancelLaunch();
    };
    const onFocus = (event: FocusEvent): void => {
      if (layer === null || !(event.target instanceof Node) || layer.contains(event.target)) return;
      layer.focus({ preventScroll: true });
    };
    const onWheel = (event: WheelEvent): void => event.preventDefault();
    // Слой с причиной отказа убирается нажатием в любое место.
    const onPress = (): void => {
      if (phase === 'failed') cancelLaunch();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusin', onFocus);
    layer?.addEventListener('wheel', onWheel, { passive: false });
    layer?.addEventListener('click', onPress);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusin', onFocus);
      layer?.removeEventListener('wheel', onWheel);
      layer?.removeEventListener('click', onPress);
    };
  }, [blocking, phase]);

  // Проверка делом: размытие включено, а кадры под слоем идут медленно —
  // слой становится плотным прямо сейчас, и устройство это запоминает.
  useEffect(() => {
    if (id === null || phase !== 'preparing' || plain || rememberedScrim() === 'blur') return undefined;
    if (typeof window.requestAnimationFrame !== 'function') return undefined;
    const started = performance.now();
    const gaps: number[] = [];
    let last = 0;
    let frame = window.requestAnimationFrame(function tick(now) {
      if (now - started >= PROBE_DELAY_MS) {
        if (last !== 0) gaps.push(now - last);
        last = now;
      }
      if (gaps.length >= PROBE_FRAMES) {
        if (slowFrames(gaps)) {
          rememberPlain();
          setPlain(true);
        }
        return;
      }
      frame = window.requestAnimationFrame(tick);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [id, phase, plain]);

  // Уход: слой доигрывает растворение и снимается. По таймеру, а не по
  // событию анимации — при «меньше движения» анимации нет и события не будет.
  useEffect(() => {
    if (id === null || (phase !== 'revealing' && phase !== 'closing')) return undefined;
    const timer = window.setTimeout(
      () => finishLaunch(id),
      phase === 'revealing' ? LAUNCH_REVEAL_MS : LAUNCH_CLOSE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [id, phase]);

  // Отмена и отказ возвращают фокус на нажатый элемент. Страница снимает с
  // него «занято» своим рендером, чуть позже нашего, а в отключённую кнопку
  // фокус не встаёт — поэтому несколько попыток подряд.
  const returnFocus = view?.returnFocus === true && phase === 'closing';
  const origin = view?.origin ?? null;
  useEffect(() => {
    if (!returnFocus || origin === null) return undefined;
    const target = origin;
    let timer = 0;
    let tries = 0;
    const attempt = (): void => {
      if (!target.isConnected) return;
      const active = document.activeElement;
      // Зритель уже ушёл фокусом в другое место — не перебиваем.
      if (active !== null && active !== document.body && active !== target && !host.current?.contains(active)) return;
      target.focus({ preventScroll: true });
      tries += 1;
      if (document.activeElement !== target && tries < 20) timer = window.setTimeout(attempt, 25);
    };
    attempt();
    return () => window.clearTimeout(timer);
  }, [returnFocus, origin]);

  if (view === null) return null;

  return (
    <div
      ref={host}
      className={`launch-layer is-${view.phase}${plain ? ' is-plain' : ''}`}
      data-phase={view.phase}
      role="dialog"
      aria-modal="true"
      aria-label="Запуск просмотра"
      tabIndex={-1}
    >
      <div className="launch-scrim" aria-hidden="true" />
      <div className="launch-fore">
        <button
          ref={back}
          type="button"
          className="player-back launch-back"
          onClick={cancelLaunch}
          aria-label={view.phase === 'preparing' ? 'Отменить запуск' : 'Назад'}
          title={view.phase === 'preparing' ? 'Отменить запуск' : 'Назад'}
          disabled={!blocking}
        >
          <BackIcon />
        </button>
        <div className="launch-wait screen-wait" role="status" aria-live="polite">
          <SunLoader className="launch-sign" />
          <div className="screen-wait-copy">
            <p className="launch-label">{view.label}</p>
            {/* Ключ по тексту: новый этап — новая строка, и она проявляется,
                а не подменяет прежнюю на месте. Причина отказа встаёт на то
                же место и читается диктором сразу. */}
            {view.stage !== null && (
              <p
                className={view.phase === 'failed' ? 'launch-stage is-error' : 'launch-stage'}
                key={view.stage}
                role={view.phase === 'failed' ? 'alert' : undefined}
              >
                {view.stage}
              </p>
            )}
            {view.detail !== null && <span className="player-preparation">{view.detail}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
